// ©2024 Austin App House. All rights reserved.
//
// Converge Checkout.js / Lightbox helpers (frontend contract).
//
// POST /api/payment/converge/session-token
//   payment: { amount, saveCard?, ssl_first_name?, ssl_last_name? }
//   vault:   { intent: 'vault', saveCard: true, ssl_first_name?, ssl_last_name? }
//   res:  { token } | string token
//
// POST /api/payment/converge/checkout-confirm
//   payment: { amount, saveCard?, transaction }
//   res:  { success, message }
//
// POST /api/payment/converge/vault-confirm
//   body: { transaction }
//   res:  { success, message, transactionId?, savedCard? }

import axios from 'axios';

export const CONVERGE_SESSION_TOKEN = '/api/payment/converge/session-token';
export const CONVERGE_CHECKOUT_CONFIRM = '/api/payment/converge/checkout-confirm';
export const CONVERGE_VAULT_CONFIRM = '/api/payment/converge/vault-confirm';

export function unwrapConvergeToken(data) {
  const body = data?.payload ?? data;
  const token = body?.token ?? body?.ssl_txn_auth_token ?? data?.token;
  if (typeof token === 'string' && token.length > 0) return token;
  return null;
}

/** Converge expects dollars with two decimal places, e.g. "100.00". */
export function formatConvergeAmount(amount) {
  const value = Number(amount);
  if (!Number.isFinite(value) || value <= 0) return null;
  return value.toFixed(2);
}

/** Converge ssl_exp_date is MMYY (no slash), e.g. "1228" for 12/28. */
export function formatConvergeExpDate(expMonth, expYear) {
  const month = String(expMonth || '').padStart(2, '0').slice(-2);
  const year = String(expYear || '').padStart(2, '0').slice(-2);
  if (!/^\d{2}$/.test(month) || !/^\d{2}$/.test(year)) return null;
  return `${month}${year}`;
}

export function buildCheckoutPaymentFields({
  token,
  cardNumber,
  expMonth,
  expYear,
  cvv,
  firstName,
  lastName,
  zip,
}) {
  const sslExpDate = formatConvergeExpDate(expMonth, expYear);
  const digits = String(cardNumber || '').replace(/\D/g, '');

  return {
    ssl_txn_auth_token: token,
    ssl_card_number: digits,
    ssl_exp_date: sslExpDate,
    ssl_cvv2cvc2: String(cvv || '').replace(/\D/g, ''),
    ...(firstName ? { ssl_first_name: firstName } : {}),
    ...(lastName ? { ssl_last_name: lastName } : {}),
    ...(zip ? { ssl_avs_zip: String(zip).trim() } : {}),
  };
}

export async function createConvergeCheckoutToken({
  amount,
  saveCard = false,
  firstName,
  lastName,
} = {}) {
  const sslAmount = formatConvergeAmount(amount);
  if (!sslAmount) throw new Error('Please enter a valid amount');

  const { data } = await axios.post(CONVERGE_SESSION_TOKEN, {
    amount: Number(sslAmount),
    saveCard: Boolean(saveCard),
    ssl_amount: sslAmount,
    ssl_transaction_type: 'ccsale',
    ssl_add_token: saveCard ? 'Y' : 'N',
    ssl_first_name: firstName,
    ssl_last_name: lastName,
  });
  const token = unwrapConvergeToken(data);
  if (!token || typeof token !== 'string') {
    throw new Error(data?.error || 'No transaction token returned');
  }
  return token;
}

/** Vault-only session token for Payment Methods (save card, no charge). */
export async function createConvergeVaultToken({
  firstName,
  lastName,
} = {}) {
  const { data } = await axios.post(CONVERGE_SESSION_TOKEN, {
    intent: 'vault',
    saveCard: true,
    ssl_transaction_type: 'ccgettoken',
    ssl_add_token: 'Y',
    ssl_first_name: firstName,
    ssl_last_name: lastName,
  });
  const token = unwrapConvergeToken(data);
  if (!token || typeof token !== 'string') {
    throw new Error(data?.error || data?.message || 'No transaction token returned');
  }
  return token;
}

export async function confirmConvergeCheckout({
  amount,
  saveCard,
  transaction,
}) {
  const { data } = await axios.post(CONVERGE_CHECKOUT_CONFIRM, {
    amount: Number(amount),
    saveCard: Boolean(saveCard),
    transaction,
  });
  return data;
}

/** Persist a vaulted Converge card after Checkout.js tokenization (no charge). */
export async function confirmConvergeVaultCard({ transaction }) {
  const { data } = await axios.post(CONVERGE_VAULT_CONFIRM, {
    transaction,
  });
  return data;
}

/** Resolve once Checkout.js exposes ConvergeEmbeddedPayment (loaded from index.html). */
export function waitForConvergeCheckout(timeoutMs = 15000) {
  return new Promise((resolve, reject) => {
    if (window.ConvergeEmbeddedPayment?.pay) {
      resolve(window.ConvergeEmbeddedPayment);
      return undefined;
    }

    const started = Date.now();
    const timer = window.setInterval(() => {
      if (window.ConvergeEmbeddedPayment?.pay) {
        window.clearInterval(timer);
        resolve(window.ConvergeEmbeddedPayment);
        return;
      }
      if (Date.now() - started >= timeoutMs) {
        window.clearInterval(timer);
        reject(new Error('Checkout.js is not loaded. Check the Checkout.js script tag in index.html.'));
      }
    }, 50);

    return undefined;
  });
}
